// Claude: New AI Chat page — buyer's primary landing page with Jumbotron header 16-02-2026
// Claude: chat history moved to Redux aiChat slice — shared with AiChatDrawer 23-03-2026
import {
  Box,
  Flex,
  Input,
  Button,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { BsSendFill } from "react-icons/bs";
import { MdDeleteOutline } from "react-icons/md";
import Jumbotron from "../../Components/Common/Jumbotron";
import { Buyerscreens } from "../../Constants/data";
import actions from "../../redux/Actions";
import { BuyerAPI } from "../../Apis/buyerAPI";
import { CommonAPI } from "../../Apis/commonApi";
import { useToastWrapper } from "../../Wrapper/toastWrapper";

type Message = {
  id: number;
  text: string;
  sender: "ai" | "user";
  timestamp: Date;
};

// Claude: Parse <button>text</button> tags out of AI message text 15-03-2026
// Returns prose (text with button tags removed) and options (each button label)
const parseOptions = (text: string): { prose: string; options: string[] } => {
  const buttonRegex = /<button>(.*?)<\/button>/gi;
  const options: string[] = [];
  let match;
  while ((match = buttonRegex.exec(text)) !== null) {
    options.push(match[1].trim());
  }
  const prose = text.replace(/<button>.*?<\/button>/gi, "").trim();
  return { prose, options };
};

const AiChat = () => {
  const dispatch = useDispatch();
  const { error: toastError } = useToastWrapper();
  const user = useSelector((state: any) => state.user);
  const {
    notification: { unreadNotifications },
  } = useSelector((state: any) => state);

  const messages: Message[] = useSelector((state: any) => state.aiChat.buyerMessages);

  const [profile, setProfile] = useState<any>();
  const [activeEventId, setActiveEventId] = useState<string>("");
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Claude: autosuggest — KB questions fetched once on mount, filtered locally 26-03-2026
  const [kbQuestions, setKbQuestions] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  // Claude: feedback flow — awaitingFeedback intercepts next message as feedback; feedbackCount capped at 3/session 26-03-2026
  const [awaitingFeedback, setAwaitingFeedback] = useState(false);
  const [feedbackCount, setFeedbackCount] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Claude: Clear chat history with confirmation prompt 17-02-2026
  const handleClearChat = () => {
    if (!window.confirm("Clear entire chat history?")) return;
    dispatch(actions.aiChat.clearBuyerMessages());
    fetchWelcome();
  };

  useEffect(() => {
    dispatch(
      actions.buyerMenu.set({
        activeMenu: Buyerscreens.AI_CHAT,
      })
    );
    fetchProfile();
    fetchActiveEvent();
    // Claude: only fetch welcome if no history yet — shared history persists via Redux 23-03-2026
    if (messages.length === 0) fetchWelcome();
    fetchKbQuestions();
  }, []);

  // Claude: fetch KB question strings for autosuggest 26-03-2026
  const fetchKbQuestions = async () => {
    try {
      const res = await CommonAPI.getAiQuestions("Buyer");
      const questions = Array.isArray(res) ? res : res?.data ?? [];
      setKbQuestions(questions);
    } catch {}
  };

  // Claude: fetch welcome message from KB on load — sets messages[0] 15-03-2026
  const fetchWelcome = async () => {
    try {
      const res = await CommonAPI.getAiWelcome("Buyer");
      const text = typeof res === "string" ? res : res?.data;
      if (text) {
        dispatch(actions.aiChat.setBuyerMessages([{ id: 1, text, sender: "ai", timestamp: new Date() }]));
      }
    } catch {}
  };

  const fetchProfile = async () => {
    try {
      const res = await BuyerAPI.getBuyerProfile();
      if (res) {
        setProfile(res.data.data);
      }
    } catch (err) {
      console.log("Error fetching profile", err);
    }
  };


  // Claude: Fetch buyer's events and use the most recent one as active event 16-02-2026
  const fetchActiveEvent = async () => {
    try {
      const res = await BuyerAPI.getEvents();
      if (res?.data?.data?.length) {
        const events = res.data.data;
        setActiveEventId(events[events.length - 1]._id);
      }
    } catch (err) {
      console.log("Error fetching events", err);
    }
  };

  // Claude: Send message — accepts optional override text for button-click shortcuts 15-03-2026
  // Claude: intercepts message as feedback when awaitingFeedback is true 26-03-2026
  const handleSend = async (overrideText?: string) => {
    const textToSend = (overrideText ?? inputText).trim();
    if (!textToSend || isLoading) return;

    const userMessage: Message = { id: Date.now(), text: textToSend, sender: "user", timestamp: new Date() };
    dispatch(actions.aiChat.addBuyerMessage(userMessage));
    if (!overrideText) setInputText("");
    setSuggestions([]);

    if (awaitingFeedback) {
      setAwaitingFeedback(false);
      try {
        await CommonAPI.submitFeedback({
          role: "Buyer",
          personName: profile?.buyerPersonName || "",
          companyName: profile?.buyerCompanyName || "",
          feedbackText: textToSend,
        });
        setFeedbackCount((c) => c + 1);
      } catch {}
      dispatch(actions.aiChat.addBuyerMessage({
        id: Date.now() + 1,
        text: "Thank you for the feedback. We will process this for further action as appropriate.",
        sender: "ai",
        timestamp: new Date(),
      }));
      return;
    }

    setIsLoading(true);
    try {
      // Claude: include all messages so backend knows what options were shown 15-03-2026
      const conversationHistory = messages.map((m) => ({
        role: m.sender === "user" ? "user" : "model",
        text: m.text,
      }));

      const res = await CommonAPI.sendAiMessage({
        message: textToSend,
        conversationHistory,
        eventId: activeEventId,
      });

      const aiResponse: Message = {
        id: Date.now() + 1,
        text: res?.data?.reply || "Sorry, I couldn't process that. Please try again.",
        sender: "ai",
        timestamp: new Date(),
      };
      dispatch(actions.aiChat.addBuyerMessage(aiResponse));
    } catch (err: any) {
      console.log("AI chat error:", err);
      dispatch(actions.aiChat.addBuyerMessage({
        id: Date.now() + 1,
        text: "Sorry, something went wrong. Please try again in a moment.",
        sender: "ai",
        timestamp: new Date(),
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Claude: Tap an option button — intercept "Feedback" locally; strip number prefix for others 26-03-2026
  const handleOptionClick = (optionText: string) => {
    const clean = optionText.replace(/^\d+\.\s+/, "");
    if (clean.toLowerCase() === "feedback") {
      if (feedbackCount >= 3) {
        toastError("You have already submitted 3 feedbacks this session.");
        return;
      }
      setAwaitingFeedback(true);
      dispatch(actions.aiChat.addBuyerMessage({
        id: Date.now(),
        text: "Please share your feedback.",
        sender: "ai",
        timestamp: new Date(),
      }));
      return;
    }
    handleSend(clean);
  };

  // Claude: Re-fetch welcome message from KB and append as new AI message 16-03-2026
  const handleShowOptions = async () => {
    try {
      const res = await CommonAPI.getAiWelcome("Buyer");
      const text = typeof res === "string" ? res : res?.data;
      if (text) {
        dispatch(actions.aiChat.addBuyerMessage({ id: Date.now(), text, sender: "ai", timestamp: new Date() }));
      }
    } catch {}
  };



  // Claude: autosuggest — filter KB questions on input change 26-03-2026
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputText(val);
    if (val.trim().length >= 2) {
      const lower = val.trim().toLowerCase();
      setSuggestions(kbQuestions.filter((q) => q.toLowerCase().includes(lower)).slice(0, 6));
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (q: string) => {
    setInputText(q);
    setSuggestions([]);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSend();
    if (e.key === "Escape") setSuggestions([]);
  };

  return (
    // Claude: Light bg for consistency with other screens 17-02-2026
    <Box
      w={"100%"}
      display={"flex"}
      flexDirection={"column"}
      minH={"100%"}
      maxH={"100%"}
      bg={"linear-gradient(180deg, #FFF5EE 0%, #FFFFFF 100%)"}
    >
      {/* Jumbotron header — moved from DashboardV2 */}
      <Jumbotron
        personName={profile?.buyerPersonName}
        companyName={profile?.buyerCompanyName}
        unreadNotifications={unreadNotifications}
        role="Buyer"
        navigateUrl="/buyer/notifications"
        profileNavigateUrl="/buyer/myaccount"
        linkedinPhoto={profile?.linkedinPhoto}
        verificationStatus={user?.verificationStatus}
      />

      {/* Scrollable message area */}
      {/* Claude: Transparent bg so outer gradient shows through 17-02-2026 */}
      <Box
        flex={1}
        overflowY={"auto"}
        px={"16px"}
        pt={"16px"}
        pb={"80px"}
      >
        <VStack spacing={3} align={"stretch"}>
          {messages.map((msg) =>
            msg.sender === "ai" ? (
              <Flex key={msg.id} justify={"flex-start"}>
                {/* Claude: AI bubble — prose above, tappable option buttons below 15-03-2026 */}
                <Box maxW={"85%"}>
                  <Box
                    bg={"gray.100"}
                    px={"14px"}
                    py={"10px"}
                    borderRadius={"16px"}
                    borderTopLeftRadius={"4px"}
                  >
                    {(() => {
                      const { prose, options } = parseOptions(msg.text);
                      return (
                        <>
                          {prose && (
                            <Text fontSize={"sm"} color={"gray.800"} whiteSpace="pre-line">
                              {prose}
                            </Text>
                          )}
                          {options.length > 0 && (
                            <Wrap mt={prose ? 3 : 0} spacing={2}>
                              {options.map((opt) => (
                                <WrapItem key={opt}>
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    borderColor={"#2E7D32"}
                                    color={"#2E7D32"}
                                    borderRadius={"full"}
                                    fontSize={"12px"}
                                    fontWeight={500}
                                    px={3}
                                    h={"28px"}
                                    isDisabled={isLoading}
                                    _hover={{ bg: "#2E7D32", color: "white" }}
                                    onClick={() => handleOptionClick(opt)}
                                  >
                                    {opt}
                                  </Button>
                                </WrapItem>
                              ))}
                            </Wrap>
                          )}
                          {/* Claude: Main Menu fine print — skip on welcome message (msg index 0) 15-03-2026 */}
                          {messages.indexOf(msg) !== 0 && (
                            <Text
                              fontSize={"10px"}
                              color={"gray.400"}
                              mt={2}
                              cursor={"pointer"}
                              _hover={{ color: "#2E7D32" }}
                              onClick={handleShowOptions}
                            >
                              Click here for Main Menu
                            </Text>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                </Box>
              </Flex>
            ) : (
              <Flex key={msg.id} justify={"flex-end"}>
                <Box
                  maxW={"80%"}
                  bg={"#2E7D32"}
                  px={"14px"}
                  py={"10px"}
                  borderRadius={"16px"}
                  borderTopRightRadius={"4px"}
                >
                  <Text fontSize={"sm"} color={"white"}>
                    {msg.text}
                  </Text>
                </Box>
              </Flex>
            )
          )}
          {isLoading && (
            <Flex justify={"flex-start"}>
              <Box
                maxW={"80%"}
                bg={"gray.100"}
                px={"14px"}
                py={"10px"}
                borderRadius={"16px"}
                borderTopLeftRadius={"4px"}
              >
                <Text fontSize={"sm"} color={"gray.500"}>
                  Thinking...
                </Text>
              </Box>
            </Flex>
          )}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>

      {/* Fixed input bar above nav bar */}
      <Box
        position={"fixed"}
        // Claude: Match layout container width exactly so layout borders frame it 17-02-2026
        bottom={"67px"}
        left={0}
        right={0}
        maxW={"596px"}
        mx={"auto"}
        bg={"white"}
        borderTop={"1px solid"}
        borderColor={"gray.200"}
        px={"12px"}
        py={"8px"}
        zIndex={100}
      >
        {/* Claude: autosuggest dropdown — shown above input when typing matches KB questions 26-03-2026 */}
        {suggestions.length > 0 && (
          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="12px" mb={2} overflow="hidden" boxShadow="sm">
            {suggestions.map((q) => (
              <Box
                key={q}
                px={4} py={"10px"}
                fontSize="sm" color="gray.700"
                cursor="pointer"
                _hover={{ bg: "#F1F8E9" }}
                borderBottom="1px solid" borderColor="gray.100"
                sx={{ "&:last-child": { borderBottom: "none" } }}
                onClick={() => handleSuggestionClick(q)}
              >
                {q}
              </Box>
            ))}
          </Box>
        )}
        <Flex alignItems={"center"} gap={"8px"}>
          {/* Claude: Clear chat button — resets conversation and removes localStorage 17-02-2026 */}
          <Button
            variant={"ghost"}
            h={"44px"}
            w={"44px"}
            minW={"44px"}
            rounded={"full"}
            color={"gray.400"}
            _hover={{ color: "red.400", bg: "red.50" }}
            onClick={handleClearChat}
            isDisabled={isLoading}
            title="Clear chat"
          >
            <MdDeleteOutline size={20} />
          </Button>
          <Input
            placeholder={"Select an option above or type here.."}
            border={"1px solid"}
            borderColor={"gray.300"}
            borderRadius={"full"}
            h={"44px"}
            px={"16px"}
            value={inputText}
            _focus={{ borderColor: "#2E7D32", boxShadow: "0 0 0 1px #2E7D32" }}
            _placeholder={{
              color: "gray.400",
              fontWeight: "400",
              fontSize: "14px",
            }}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
          />
          <Button
            bg={"#2E7D32"}
            h={"44px"}
            w={"44px"}
            minW={"44px"}
            rounded={"full"}
            _hover={{ bg: "#1B5E20" }}
            onClick={() => handleSend()}
            isDisabled={isLoading}
          >
            <BsSendFill size={18} color={"white"} />
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};

export default AiChat;
